import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Building2, Target, Activity, ExternalLink, Users, FileText, AlertTriangle, AlertCircle, TrendingUp, Clock, ChevronLeft, ChevronRight, Mail, Phone, LinkedinIcon, Crown, Tag, Calendar as CalendarIcon, DollarSign, Download, Upload, Edit, Trash2, Eye, StickyNote, Building, User, Linkedin } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { ImageUpload } from "@/components/ui/image-upload";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { useDebounce } from "@/hooks/use-debounce";
import { useToast } from "@/hooks/use-toast";
import { useCreateClient } from "@/features/clients";
import { format } from "date-fns";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as DateCalendar } from "@/components/ui/calendar";
import ClientNotes from "@/components/clients/ClientNotes";
import HighlightedClientNotes from "@/components/clients/HighlightedClientNotes";
import ClientExploitation from "@/components/clients/ClientExploitation";
import { useIndustries } from "@/features/industries";
import { useClientKPIs } from "@/hooks/useClientKPIs";
import ClientKPIBadges from "@/components/clients/ClientKPIBadges";
import { ClientInitiativesTab } from "@/components/clients/ClientInitiativesTab";
import { ClientBusinessTab } from "@/components/clients/ClientBusinessTab";
import { cn } from "@/lib/utils";

// Activity types configuration (copied from Activities page)
const activityTypes = [
  { 
    value: "email", 
    label: "Email", 
    icon: Mail, 
    color: "bg-blue-500 hover:bg-blue-600 text-white",
    iconColor: "text-blue-600",
    bgColor: "bg-blue-50"
  },
  { 
    value: "call", 
    label: "Call", 
    icon: Phone, 
    color: "bg-green-500 hover:bg-green-600 text-white",
    iconColor: "text-green-600",
    bgColor: "bg-green-50"
  },
  { 
    value: "meeting", 
    label: "Meet", 
    icon: CalendarIcon, 
    color: "bg-purple-500 hover:bg-purple-600 text-white",
    iconColor: "text-purple-600",
    bgColor: "bg-purple-50"
  },
  { 
    value: "linkedin", 
    label: "LinkedIn", 
    icon: Linkedin, 
    color: "bg-sky-600 hover:bg-sky-700 text-white",
    iconColor: "text-sky-600",
    bgColor: "bg-sky-50"
  }
];

// Response status configuration (copied from Activities page)
const responseStatusOptions = [
  { value: "no_response", label: "No response", color: "bg-gray-100 text-gray-700" },
  { value: "not_interested", label: "Not interested", color: "bg-red-100 text-red-700" },
  { value: "interested", label: "Interested", color: "bg-yellow-100 text-yellow-700" },
  { value: "proposal", label: "Proposal", color: "bg-green-100 text-green-700" }
];

// Funnel stage display mapping
const funnelStageDisplayMap = {
  'aware': 'Aware',
  'contacted': 'Contacted',
  'pitched': 'Pitched',
  'closed': 'Closed',
  'recurring': 'Recurring'
};

const funnelStageColors = {
  'aware': 'bg-gray-100 text-gray-800',
  'contacted': 'bg-blue-100 text-blue-800',
  'pitched': 'bg-yellow-100 text-yellow-800',
  'closed': 'bg-green-100 text-green-800',
  'recurring': 'bg-purple-100 text-purple-800'
};

const statusColors = {
  'active': 'bg-green-100 text-green-800',
  'prospect': 'bg-blue-100 text-blue-800',
  'inactive': 'bg-gray-100 text-gray-800'
};

const projectStatusColors = {
  'pipeline': 'bg-yellow-100 text-yellow-800',
  'in_progress': 'bg-blue-100 text-blue-800', 
  'completed': 'bg-green-100 text-green-800',
  'on_hold': 'bg-orange-100 text-orange-800',
  'cancelled': 'bg-red-100 text-red-800'
};

const proposalStatusColors = {
  accepted: 'bg-green-100 text-green-800',
  sent: 'bg-blue-100 text-blue-800',
  draft: 'bg-gray-100 text-gray-800',
  rejected: 'bg-red-100 text-red-800',
  pending: 'bg-yellow-100 text-yellow-800'
} as const;

// Country name to ISO code mapping (minimal, extend as needed)
const countryNameToCode: Record<string, string> = {
  greece: 'GR', romania: 'RO', 'united states': 'US', 'united kingdom': 'GB',
  germany: 'DE', france: 'FR', italy: 'IT', spain: 'ES', portugal: 'PT',
  netherlands: 'NL', belgium: 'BE', sweden: 'SE', norway: 'NO', denmark: 'DK',
  finland: 'FI', poland: 'PL', austria: 'AT', switzerland: 'CH', ireland: 'IE',
  'czech republic': 'CZ', slovakia: 'SK', slovenia: 'SI', hungary: 'HU', bulgaria: 'BG',
  croatia: 'HR', serbia: 'RS', albania: 'AL', turkey: 'TR', cyprus: 'CY',
  russia: 'RU', ukraine: 'UA', canada: 'CA', mexico: 'MX', brazil: 'BR',
  argentina: 'AR', chile: 'CL', australia: 'AU', 'new zealand': 'NZ', china: 'CN',
  japan: 'JP', 'south korea': 'KR', india: 'IN', singapore: 'SG', 'united arab emirates': 'AE',
  'saudi arabia': 'SA', 'south africa': 'ZA', egypt: 'EG', morocco: 'MA', nigeria: 'NG', kenya: 'KE'
};

const toFlagEmoji = (code: string) =>
  code
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .split('')
    .map(c => String.fromCodePoint(127397 + c.charCodeAt(0)))
    .join('');

const getCountryFlag = (country?: string | null) => {
  if (!country) return '';
  const trimmed = country.trim();
  let code = '';
  if (/^[A-Za-z]{2}$/.test(trimmed)) {
    code = trimmed.toUpperCase();
  } else {
    code = countryNameToCode[trimmed.toLowerCase()] || '';
  }
  return code ? toFlagEmoji(code) : '';
};

// Types for editing functionality
interface EditablePersons {
  [key: string]: boolean;
}

interface PersonEditData {
  [key: string]: {
    first_name: string;
    last_name: string;
  };
}

export default function Clients() {
  const navigate = useNavigate();
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [industryFilter, setIndustryFilter] = useState<string>("all");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddPersonModalOpen, setIsAddPersonModalOpen] = useState(false);
  const [editingPersons, setEditingPersons] = useState<EditablePersons>({});
  const [personEditData, setPersonEditData] = useState<PersonEditData>({});
  const [newPerson, setNewPerson] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    position: "",
    currentClient: "",
    isDecisionMaker: false,
    notes: "",
    tags: "",
    avatarUrl: ""
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user, userRole, loading } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: industries = [], isLoading: industriesLoading } = useIndustries();
  
  // Real-time updates for people changes
  useEffect(() => {
    if (!selectedClient?.id) return;

    const channel = supabase
      .channel('people-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'private',
          table: 'people',
          filter: `current_client_id=eq.${selectedClient.id}`
        },
        () => {
          // Invalidate queries when people data changes
          queryClient.invalidateQueries({ queryKey: ["client-details", selectedClient.id] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedClient?.id, queryClient]);
  
  // Debounce search term for better performance
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // New client form state
  const [newClient, setNewClient] = useState({
    name: "",
    industry: "",
    country: "",
    logo_url: "",
    status: "prospect",
    funnel_stage: "aware",
    payment_terms: "60 Days",
  });

  // Edit client form state
  const [editClient, setEditClient] = useState({
    id: "",
    name: "",
    industry: "",
    country: "",
    logo_url: "",
    status: "prospect",
    funnel_stage: "aware",
    payment_terms: "60 Days",
  });

// Proposal filters state
  const [proposalFilter, setProposalFilter] = useState<'all'|'accepted'|'pending'|'sent'|'draft'|'rejected'>('all');
  const [proposalTimeRange, setProposalTimeRange] = useState<'ytd'|'last_year'|'ever'|'custom'>('ytd');
  const [customRange, setCustomRange] = useState<{ from?: Date; to?: Date }>({});

  // Role-based access control  
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>You must be logged in to access this page. Please go to the Auth page to sign in.</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Fetch clients from Supabase with search (no pagination - show all)
  const { data: clientsData, isLoading, error } = useQuery({
    queryKey: ["clients", user?.id],
    queryFn: async () => {
      let query = supabase
        .from("clients")
        .select("*", { count: 'exact' })
        .order('name');

      const { data, error, count } = await query;
      if (error) throw error;
      
      return {
        clients: data || [],
        totalCount: count || 0,
        totalPages: 1
      };
    },
    enabled: !!user,
  });

  const clients = clientsData?.clients || [];

  // Filter clients by search term and industry
  const filteredClients = useMemo(() => {
    if (!clients) return [];
    
    return clients.filter(client => {
      // Search filter
      const matchesSearch = !debouncedSearchTerm || 
        client.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        (client.industry && client.industry.toLowerCase().includes(debouncedSearchTerm.toLowerCase())) ||
        (client.country && client.country.toLowerCase().includes(debouncedSearchTerm.toLowerCase()));
      
      // Industry filter
      const matchesIndustry = industryFilter === "all" || client.industry === industryFilter;
      
      return matchesSearch && matchesIndustry;
    });
  }, [clients, debouncedSearchTerm, industryFilter]);

  // Fetch client KPIs for all clients
  const clientIds = clients.map(c => c.id);
  const { data: clientKPIs, isLoading: kpisLoading } = useClientKPIs(clientIds);

  // Fetch client statistics (projects, proposals, activities) - Using Promise.all for parallel fetching
  const { data: clientStats } = useQuery({
    queryKey: ["client-stats"],
    queryFn: async () => {
      // Helper function to ensure stats object exists
      const ensureStats = (clientId: string, stats: Record<string, any>) => {
        if (!stats[clientId]) {
          stats[clientId] = {
            projectCount: 0,
            totalValue: 0,
            activeProjects: 0,
            proposalCount: 0,
            p2yProjectsCount: 0,
            p2yProposalsCount: 0,
            lastActivity: null
          };
        }
      };

      // Fetch all data in parallel using Promise.all
      const [projectStats, proposalStats, activityStats] = await Promise.all([
        supabase
          .from("projects")
          .select("client_id, value, status, created_at")
          .then(({ data, error }) => {
            if (error) throw error;
            return data || [];
          }),
        
        supabase
          .from("proposals")
          .select("client_id, status, value, final_value, created_at")
          .then(({ data, error }) => {
            if (error) throw error;
            return data || [];
          }),
        
        supabase
          .from("activities")
          .select("client_id, activity_date")
          .order('activity_date', { ascending: false })
          .then(({ data, error }) => {
            if (error) throw error;
            return data || [];
          })
      ]);

      // Process statistics with improved logic
      const stats: Record<string, any> = {};
      const twoYearsAgo = new Date();
      twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
      // Process project stats
      projectStats.forEach(project => {
        ensureStats(project.client_id, stats);
        stats[project.client_id].projectCount++;
        stats[project.client_id].totalValue += Number(project.value) || 0;
        if (project.status === 'in_progress') {
          stats[project.client_id].activeProjects++;
        }
        if (project.created_at && new Date(project.created_at) >= twoYearsAgo) {
          stats[project.client_id].p2yProjectsCount++;
        }
      });

      // Process proposal stats
      proposalStats.forEach(proposal => {
        ensureStats(proposal.client_id, stats);
        stats[proposal.client_id].proposalCount++;
        if (proposal.created_at && new Date(proposal.created_at) >= twoYearsAgo) {
          stats[proposal.client_id].p2yProposalsCount++;
        }
        // Add accepted proposal value to total value - use final_value if available, otherwise use initial value
        if (proposal.status === 'accepted') {
          const proposalValue = proposal.final_value || proposal.value;
          stats[proposal.client_id].totalValue += Number(proposalValue) || 0;
        }
      });

      // Process activity stats
      activityStats.forEach(activity => {
        ensureStats(activity.client_id, stats);
        if (!stats[activity.client_id].lastActivity) {
          stats[activity.client_id].lastActivity = activity.activity_date;
        }
      });

      return stats;
    },
  });

  // Fetch data for selected client details - Using Promise.all for parallel fetching
  const { data: selectedClientDetails } = useQuery({
    queryKey: ["client-details", selectedClient?.id],
    queryFn: async () => {
      if (!selectedClient) return null;

      // Import activities service for proper data fetching
      const { activitiesService } = await import('@/services/activitiesService');

      // Fetch activities, proposals, people, projects, and documents in parallel
      const [allActivities, proposals, people, projects, documents] = await Promise.all([
        activitiesService.getActivities(),
        
        supabase
          .from("proposals")
          .select("*")
          .eq("client_id", selectedClient.id)
          .order('created_at', { ascending: false })
          .then(({ data, error }) => {
            if (error) throw error;
            return data || [];
          }),
        
        supabase
          .rpc("get_people_secure")
          .then(async ({ data, error }) => {
            const { data: user } = await supabase.auth.getUser();
            console.log("DEBUG People Query:", {
              clientId: selectedClient.id,
              clientName: selectedClient.name,
              data: data,
              error: error,
              authUser: user?.user?.id,
              authEmail: user?.user?.email
            });
            
            if (error) {
              console.error("People query error:", error);
              throw error;
            }
            // Filter people for this specific client
            const clientPeople = (data || []).filter((person: any) => 
              person.current_client_id === selectedClient.id
            );
            console.log("DEBUG Filtered People:", clientPeople);
            return clientPeople;
          }),
        
        supabase
          .from("projects")
          .select("*")
          .eq("client_id", selectedClient.id)
          .order('created_at', { ascending: false })
          .then(({ data, error }) => {
            if (error) throw error;
            return data || [];
          }),
        
        supabase
          .from("documents")
          .select("*")
          .eq("client_id", selectedClient.id)
          .order('created_at', { ascending: false })
          .then(({ data, error }) => {
            if (error) throw error;
            return data || [];
          })
      ]);

      // Filter activities for this specific client
      const activities = allActivities.filter(activity => activity.client_id === selectedClient.id);

      return {
        activities,
        proposals,
        people,
        projects,
        documents
      };
    },
    enabled: !!selectedClient,
  });

  const filteredProposals = useMemo(() => {
    const list: any[] = selectedClientDetails?.proposals || [];
    // Compute date range
    let from: Date | undefined, to: Date | undefined;
    const now = new Date();
    if (proposalTimeRange === 'ytd') {
      from = new Date(now.getFullYear(), 0, 1);
      to = now;
    } else if (proposalTimeRange === 'last_year') {
      from = new Date(now.getFullYear() - 1, 0, 1);
      to = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
    } else if (proposalTimeRange === 'custom') {
      from = customRange.from;
      to = customRange.to ? new Date(customRange.to.getFullYear(), customRange.to.getMonth(), customRange.to.getDate(), 23, 59, 59, 999) : undefined;
    }

    return list
      .filter((p) => (proposalFilter === 'all' ? true : p.status === proposalFilter))
      .filter((p) => {
        if (!from && !to) return true; // 'ever'
        const created = new Date(p.created_at);
        if (from && created < from) return false;
        if (to && created > to) return false;
        return true;
      });
  }, [selectedClientDetails?.proposals, proposalFilter, proposalTimeRange, customRange.from, customRange.to]);

  const proposalsCount = filteredProposals.length;
  const proposalsTotal = useMemo(() =>
    filteredProposals.reduce((sum: number, p: any) => sum + Number(p.final_value ?? p.value ?? 0), 0)
  , [filteredProposals]);

  // Add client mutation using proper client service
  const createClientMutation = useCreateClient();

  const addClientMutation = {
    ...createClientMutation,
    mutate: (clientData: typeof newClient) => {
      createClientMutation.mutate({
        name: clientData.name,
        industry: clientData.industry || undefined,
        country: clientData.country || undefined,
        logo_url: clientData.logo_url || undefined,
        status: clientData.status,
        funnel_stage: clientData.funnel_stage,
      });
    }
  };

  // Handle success/error states
  useEffect(() => {
    if (createClientMutation.isSuccess && createClientMutation.data) {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      setIsAddModalOpen(false);
      setSelectedClient(createClientMutation.data);
      toast({
        title: "Success", 
        description: "Client added successfully",
      });
      // Reset form
      setNewClient({
        name: "",
        industry: "",
        country: "",
        logo_url: "",
        status: "prospect",
        funnel_stage: "aware",
        payment_terms: "60 Days",
      });
    }
    
    if (createClientMutation.isError) {
      toast({
        title: "Error",
        description: "Failed to add client: " + createClientMutation.error?.message,
        variant: "destructive",
      });
    }
  }, [createClientMutation.isSuccess, createClientMutation.isError, createClientMutation.data, createClientMutation.error]);

  const editClientMutation = useMutation({
    mutationFn: async (clientData: typeof editClient) => {
      // No org_id needed - direct update
      const { data, error } = await supabase
        .from("clients")
        .update({
          name: clientData.name,
          industry: clientData.industry || null,
          country: clientData.country || null,
          logo_url: clientData.logo_url || null,
          status: clientData.status,
          funnel_stage: clientData.funnel_stage,
          payment_terms: clientData.payment_terms || "60 Days",
          updated_at: new Date().toISOString()
        })
        .eq('id', clientData.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (updatedClient) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["client-stats"] });
      setIsEditModalOpen(false);
      setSelectedClient(updatedClient);
      toast({
        title: "Success",
        description: "Client updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: "Failed to update client: " + error.message,
        variant: "destructive",
      });
    }
  });

  // Delete client mutation
  const deleteClientMutation = useMutation({
    mutationFn: async (clientId: string) => {
      // First, update all people to remove association with this client
      const { error: peopleError } = await supabase.rpc('remove_client_association_from_people', {
        target_client_id: clientId
      });

      if (peopleError) throw peopleError;

      // Update all activities to remove association with this client
      const { error: activitiesError } = await supabase
        .from("activities")
        .update({ client_id: null })
        .eq('client_id', clientId);

      if (activitiesError) throw activitiesError;

      // Update all proposals to remove association with this client (if needed)
      const { error: proposalsError } = await supabase
        .from("proposals")
        .delete()
        .eq('client_id', clientId);

      if (proposalsError) throw proposalsError;

      // Update all projects to remove association with this client (if needed)
      const { error: projectsError } = await supabase
        .from("projects")
        .delete()
        .eq('client_id', clientId);

      if (projectsError) throw projectsError;

      // Update all documents to remove association with this client
      const { error: documentsError } = await supabase
        .from("documents")
        .delete()
        .eq('client_id', clientId);

      if (documentsError) throw documentsError;

      // Update all invoices to remove association with this client
      const { error: invoicesError } = await supabase
        .from("invoices")
        .delete()
        .eq('client_id', clientId);

      if (invoicesError) throw invoicesError;

      // Then delete the client
      const { error } = await supabase
        .from("clients")
        .delete()
        .eq('id', clientId);

      if (error) throw error;
      return clientId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["client-stats"] });
      queryClient.invalidateQueries({ queryKey: ["client-details"] });
      queryClient.invalidateQueries({ queryKey: ["people"] });
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      queryClient.invalidateQueries({ queryKey: ["proposals"] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      setSelectedClient(null);
      toast({
        title: "Success",
        description: "Client and all associated records deleted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: "Failed to delete client: " + error.message,
        variant: "destructive",
      });
    }
  });

  // Add person mutation with better duplicate prevention
  const addPersonMutation = useMutation({
    mutationFn: async (personData: typeof newPerson) => {
      const clientId = selectedClient?.id;
      if (!clientId) throw new Error("No client selected");

      console.log("🔵 Creating person with data:", personData);

      const { data: personId, error } = await supabase
        .rpc("insert_person_secure", {
          person_data: {
            first_name: personData.firstName,
            last_name: personData.lastName,
            email: personData.email || null,
            phone: personData.phone || null,
            position: personData.position || null,
            status: "active",
            is_decision_maker: personData.isDecisionMaker,
            notes: personData.notes || null,
            current_client_id: clientId
          }
        });

      if (error) {
        console.error("❌ Error creating person:", error);
        throw error;
      }

      console.log("✅ Person created successfully:", personId);
      return personId;
    },
    onSuccess: () => {
      console.log("🎉 Person creation mutation succeeded");
      queryClient.invalidateQueries({ queryKey: ["client-details", selectedClient?.id] });
      queryClient.invalidateQueries({ queryKey: ["people"] });
      
      toast({
        title: "Success",
        description: "Person added successfully",
      });
      
      setIsAddPersonModalOpen(false);
      setNewPerson({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        position: "",
        notes: "",
        currentClient: "",
        isDecisionMaker: false,
        tags: "",
        avatarUrl: ""
      });
    },
    onError: (error: any) => {
      console.error("❌ Person creation failed:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to add person",
        variant: "destructive",
      });
    }
  });

  const uploadFileMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!selectedClient?.id) {
        throw new Error("No client selected");
      }

      // Upload to storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${selectedClient.id}/${Date.now()}_${Math.random().toString(36).slice(2)}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('public-files')
        .upload(fileName, file);
      
      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('public-files')
        .getPublicUrl(fileName);

      // Insert metadata into documents table
      const { error: insertError } = await supabase
        .from('documents')
        .insert({
          client_id: selectedClient.id,
          name: file.name,
          file_path: fileName,
          drive_url: publicUrl,
          mime_type: file.type,
          size_bytes: file.size,
        });

      if (insertError) throw insertError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-details', selectedClient?.id] });
      toast({
        title: "Success",
        description: "File uploaded successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to upload file",
        variant: "destructive",
      });
    },
  });

  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadFileMutation.mutate(file);
      // Reset input
      e.target.value = '';
    }
  };

  const handleAddPerson = () => {
    if (!selectedClient) {
      toast({
        title: "Error",
        description: "No client selected",
        variant: "destructive",
      });
      return;
    }
    
    // Pre-fill the current client
    setNewPerson(prev => ({
      ...prev,
      currentClient: selectedClient.name
    }));
    
    setIsAddPersonModalOpen(true);
  };

  const handleSubmitAddPerson = (e?: React.FormEvent) => {
    e?.preventDefault();
    
    // Prevent duplicate submissions
    if (addPersonMutation.isPending) {
      return;
    }
    
    if (!newPerson.firstName || !newPerson.lastName) {
      toast({
        title: "Error",
        description: "First name and last name are required",
        variant: "destructive",
      });
      return;
    }
    
    addPersonMutation.mutate(newPerson);
  };

  // Delete person mutation
  const deletePersonMutation = useMutation({
    mutationFn: async (personId: string) => {
      // First delete related employment history
      const { error: empError } = await supabase
        .from("employment_history")
        .delete()
        .eq("person_id", personId);

      if (empError) throw empError;

      // Delete related activities
      const { error: actError } = await supabase
        .from("activities")
        .delete()
        .eq("person_id", personId);

      if (actError) throw actError;

      // Delete related proposal recipients
      const { error: propError } = await supabase
        .from("proposal_recipients")
        .delete()
        .eq("person_id", personId);

      if (propError) throw propError;

      // Finally delete the person - don't fail if person record doesn't exist
      const { error } = await supabase
        .rpc("delete_person_secure", {
          person_id: personId
        });

      if (error) {
        throw new Error(error.message || "Failed to delete person");
      }

      // Success - all related records deleted, person deletion result doesn't matter
      // as the related records are the important ones for data integrity
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["people"] });
      queryClient.invalidateQueries({ queryKey: ["client-details"] });
      toast({
        title: "Success",
        description: "Person deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete person: " + error.message,
        variant: "destructive",
      });
    }
  });

  // Person editing functions
  const startEditingPerson = (person: any) => {
    setEditingPersons(prev => ({ ...prev, [person.id]: true }));
    setPersonEditData(prev => ({
      ...prev,
      [person.id]: {
        first_name: person.first_name || '',
        last_name: person.last_name || ''
      }
    }));
  };

  const cancelEditingPerson = (personId: string) => {
    setEditingPersons(prev => ({ ...prev, [personId]: false }));
    setPersonEditData(prev => {
      const newData = { ...prev };
      delete newData[personId];
      return newData;
    });
  };

  const updatePersonEditData = (personId: string, field: 'first_name' | 'last_name', value: string) => {
    setPersonEditData(prev => ({
      ...prev,
      [personId]: {
        ...prev[personId],
        [field]: value
      }
    }));
  };

  const savePersonChanges = async (personId: string) => {
    try {
      const editData = personEditData[personId];
      const { error } = await supabase.rpc('update_person_secure', {
        person_id: personId,
        person_data: {
          first_name: editData.first_name.trim(),
          last_name: editData.last_name.trim()
        }
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Person details updated successfully",
      });

      // Refresh the client details
      queryClient.invalidateQueries({ queryKey: ["client-details", selectedClient?.id] });
      
      // Exit edit mode
      cancelEditingPerson(personId);
      
    } catch (error: any) {
      console.error('Error updating person:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update person details",
        variant: "destructive",
      });
    }
  };

  // Auto-select first client when data loads (only if no client selected)
  useEffect(() => {
    if (filteredClients.length > 0 && !selectedClient) {
      setSelectedClient(filteredClients[0]);
    }
  }, [filteredClients, selectedClient]);

  // Handle search changes
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  // Handle form submission
  const handleAddClient = () => {
    if (!newClient.name.trim()) {
      toast({
        title: "Error",
        description: "Client name is required",
        variant: "destructive",
      });
      return;
    }
    addClientMutation.mutate(newClient);
  };

  // Handle edit form submission
  const handleEditClient = () => {
    if (!editClient.name.trim()) {
      toast({
        title: "Error",
        description: "Client name is required",
        variant: "destructive",
      });
      return;
    }
    editClientMutation.mutate(editClient);
  };

  // Handle edit button click
  const handleEditButtonClick = () => {
    if (selectedClient) {
      setEditClient({
        id: selectedClient.id,
        name: selectedClient.name || "",
        industry: selectedClient.industry || "",
        country: selectedClient.country || "",
        logo_url: selectedClient.logo_url || "",
        status: selectedClient.status || "prospect",
        funnel_stage: selectedClient.funnel_stage || "aware",
        payment_terms: selectedClient.payment_terms || "60 Days",
      });
      setIsEditModalOpen(true);
    }
  };

  // Handle delete confirmation
  const handleDeleteClient = () => {
    if (selectedClient) {
      deleteClientMutation.mutate(selectedClient.id);
    }
  };

  // Loading skeleton
  const ClientSkeleton = () => (
    <div className="flex h-[calc(100vh-120px)]">
      <div className="w-80 border-r border-border bg-card p-4">
        <Skeleton className="h-10 w-full mb-4" />
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </div>
      <div className="flex-1 p-6">
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-3 gap-8">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-8 w-16" />
              </div>
            ))}
          </div>
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    </div>
  );

  // Production ready - removed debug logs

  if (isLoading) {
    return <ClientSkeleton />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Failed to load clients: {error.message}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (filteredClients.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <Building2 className="h-12 w-12 text-muted-foreground mx-auto" />
          <h3 className="text-lg font-medium">No clients found</h3>
          <p className="text-muted-foreground">
            {searchTerm ? "Try adjusting your search terms." : "Get started by adding your first client."}
          </p>
        </div>
      </div>
    );
  }

  const clientStatistics = clientStats?.[selectedClient?.id] || {
    projectCount: 0,
    totalValue: 0,
    activeProjects: 0,
    proposalCount: 0,
    lastActivity: null
  };

  return (
    <div className="flex h-[calc(100vh-120px)]">
      {/* Left Sidebar - Clients List */}
      <div className="w-80 border-r border-border bg-card">
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-foreground">
              Clients {!isLoading && (
                <span className="text-muted-foreground">({clients.length})</span>
              )}
            </h2>
            <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-primary hover:bg-primary/90">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Client
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Add New Client</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name">Client Name *</Label>
                    <Input 
                      id="name"
                      value={newClient.name}
                      onChange={(e) => setNewClient({...newClient, name: e.target.value})}
                      placeholder="Company name"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="industry">Industry</Label>
                      <Select value={newClient.industry} onValueChange={(value) => setNewClient({...newClient, industry: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                        <SelectContent>
                          {industries.map((industry) => (
                            <SelectItem key={industry.id} value={industry.name}>
                              {industry.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="country">Country</Label>
                      <Input 
                        id="country"
                        value={newClient.country}
                        onChange={(e) => setNewClient({...newClient, country: e.target.value})}
                        placeholder="e.g. Greece"
                      />
                    </div>
                  </div>

                   <ImageUpload
                     value={newClient.logo_url}
                     onChange={(url) => setNewClient({...newClient, logo_url: url})}
                     bucket="client-logos"
                     variant="square"
                     size="md"
                     label="Logo"
                   />

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="status">Status</Label>
                      <Select value={newClient.status} onValueChange={(value) => setNewClient({...newClient, status: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="prospect">Prospect</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="funnel_stage">Funnel Stage</Label>
                      <Select value={newClient.funnel_stage} onValueChange={(value) => setNewClient({...newClient, funnel_stage: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="aware">Aware</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="pitched">Pitched</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                          <SelectItem value="recurring">Recurring</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="payment_terms">Payment Terms</Label>
                      <Select value={newClient.payment_terms} onValueChange={(value) => setNewClient({...newClient, payment_terms: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Immediately">Immediately</SelectItem>
                          <SelectItem value="30 Days">30 Days</SelectItem>
                          <SelectItem value="60 Days">60 Days</SelectItem>
                          <SelectItem value="90 Days">90 Days</SelectItem>
                          <SelectItem value="120 Days">120 Days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>


                  <Button 
                    onClick={handleAddClient} 
                    className="w-full" 
                    disabled={addClientMutation.isPending}
                  >
                    {addClientMutation.isPending ? "Adding..." : "Add Client"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Edit Client Modal */}
            <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Edit Client</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="edit-name">Client Name *</Label>
                    <Input 
                      id="edit-name"
                      value={editClient.name}
                      onChange={(e) => setEditClient({...editClient, name: e.target.value})}
                      placeholder="Company name"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="edit-industry">Industry</Label>
                      <Select value={editClient.industry} onValueChange={(value) => setEditClient({...editClient, industry: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                        <SelectContent>
                          {industries.map((industry) => (
                            <SelectItem key={industry.id} value={industry.name}>
                              {industry.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="edit-country">Country</Label>
                      <Input 
                        id="edit-country"
                        value={editClient.country}
                        onChange={(e) => setEditClient({...editClient, country: e.target.value})}
                        placeholder="e.g. Greece"
                      />
                    </div>
                  </div>

                   <ImageUpload
                     value={editClient.logo_url}
                     onChange={(url) => setEditClient({...editClient, logo_url: url})}
                     bucket="client-logos"
                     variant="square"
                     size="md"
                     label="Logo"
                   />

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="edit-status">Status</Label>
                      <Select value={editClient.status} onValueChange={(value) => setEditClient({...editClient, status: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="prospect">Prospect</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                     <div>
                       <Label htmlFor="edit-funnel_stage">Funnel Stage</Label>
                       <Select value={editClient.funnel_stage} onValueChange={(value) => setEditClient({...editClient, funnel_stage: value})}>
                         <SelectTrigger>
                           <SelectValue />
                         </SelectTrigger>
                         <SelectContent>
                           <SelectItem value="aware">Aware</SelectItem>
                           <SelectItem value="contacted">Contacted</SelectItem>
                           <SelectItem value="pitched">Pitched</SelectItem>
                           <SelectItem value="closed">Closed</SelectItem>
                           <SelectItem value="recurring">Recurring</SelectItem>
                         </SelectContent>
                       </Select>
                     </div>
                   </div>
                   <div>
                     <Label htmlFor="edit-payment_terms">Payment Terms</Label>
                     <Select value={editClient.payment_terms} onValueChange={(value) => setEditClient({...editClient, payment_terms: value})}>
                       <SelectTrigger>
                         <SelectValue />
                       </SelectTrigger>
                       <SelectContent>
                         <SelectItem value="Immediately">Immediately</SelectItem>
                         <SelectItem value="30 Days">30 Days</SelectItem>
                         <SelectItem value="60 Days">60 Days</SelectItem>
                         <SelectItem value="90 Days">90 Days</SelectItem>
                         <SelectItem value="120 Days">120 Days</SelectItem>
                       </SelectContent>
                     </Select>
                   </div>


                  <Button 
                    onClick={handleEditClient} 
                    className="w-full" 
                    disabled={editClientMutation.isPending}
                  >
                    {editClientMutation.isPending ? "Updating..." : "Update Client"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Add Person Modal */}
            <Dialog open={isAddPersonModalOpen} onOpenChange={setIsAddPersonModalOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Add New Person</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmitAddPerson} className="space-y-4">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input 
                        value={newPerson.firstName}
                        onChange={(e) => setNewPerson({...newPerson, firstName: e.target.value})}
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input 
                        value={newPerson.lastName}
                        onChange={(e) => setNewPerson({...newPerson, lastName: e.target.value})}
                        placeholder="Doe"
                      />
                    </div>
                  </div>

                  <ImageUpload 
                    value={newPerson.avatarUrl}
                    onChange={(url) => setNewPerson({...newPerson, avatarUrl: url})}
                    bucket="people-avatars"
                    variant="circle"
                    size="md"
                    label="Avatar"
                  />
                  
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input 
                      type="email"
                      value={newPerson.email}
                      onChange={(e) => setNewPerson({...newPerson, email: e.target.value})}
                      placeholder="email@company.com"
                    />
                  </div>

                 <div>
                   <Label htmlFor="phone">Phone</Label>
                   <Input 
                     value={newPerson.phone}
                     onChange={(e) => setNewPerson({...newPerson, phone: e.target.value})}
                     placeholder="+30 210 123 4567"
                   />
                 </div>

                 <div>
                   <Label htmlFor="position">Position</Label>
                   <Input 
                     value={newPerson.position}
                     onChange={(e) => setNewPerson({...newPerson, position: e.target.value})}
                     placeholder="Job title"
                   />
                 </div>

                 <div>
                   <Label htmlFor="currentClient">Current Company</Label>
                   <Input 
                     value={newPerson.currentClient}
                     readOnly
                     className="bg-muted"
                   />
                   <p className="text-sm text-muted-foreground mt-1">This person will be added to {selectedClient?.name}</p>
                 </div>

                 <div className="flex items-center space-x-2">
                   <Switch 
                     checked={newPerson.isDecisionMaker}
                     onCheckedChange={(checked) => setNewPerson({...newPerson, isDecisionMaker: checked})}
                   />
                   <Label>Decision Maker</Label>
                 </div>

                 <div>
                   <Label htmlFor="tags">Tags (comma separated)</Label>
                   <Input 
                     value={newPerson.tags}
                     onChange={(e) => setNewPerson({...newPerson, tags: e.target.value})}
                     placeholder="decision-maker, key-contact"
                   />
                 </div>

                 <div>
                   <Label htmlFor="notes">Notes</Label>
                   <Textarea 
                     value={newPerson.notes}
                     onChange={(e) => setNewPerson({...newPerson, notes: e.target.value})}
                     placeholder="Additional notes"
                     rows={3}
                   />
                 </div>

                 <Button 
                   type="submit"
                   className="w-full"
                   disabled={addPersonMutation.isPending}
                 >
                   {addPersonMutation.isPending ? "Adding..." : "Add Person"}
                 </Button>
               </form>
             </DialogContent>
           </Dialog>
          </div>
          
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-10"
            />
          </div>
          
          {/* Industry Filter */}
          <div className="mt-3">
            <Select value={industryFilter} onValueChange={setIndustryFilter}>
              <SelectTrigger className="w-full bg-background border-border">
                <SelectValue placeholder="Filter by industry" />
              </SelectTrigger>
              <SelectContent className="bg-background border-border shadow-lg z-50">
                <SelectItem value="all" className="hover:bg-muted cursor-pointer">All Industries</SelectItem>
                {industries.map((industry) => (
                  <SelectItem 
                    key={industry.id} 
                    value={industry.name}
                    className="hover:bg-muted cursor-pointer"
                  >
                    {industry.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="p-2 max-h-[calc(100vh-300px)] overflow-y-auto">
          {filteredClients.map((client) => {
            const kpiData = clientKPIs?.[client.id];
            
            return (
              <div
                key={client.id}
                onClick={() => setSelectedClient(client)}
                className={`p-3 rounded-lg cursor-pointer transition-colors mb-1 ${
                  selectedClient?.id === client.id
                    ? "bg-primary/10 border border-primary/20"
                    : "hover:bg-muted/50"
                }`}
              >
                 <div className="flex items-center justify-between mb-2">
                   <div className="flex items-center gap-3 flex-1 min-w-0">
                     {/* Square Logo */}
                     <div className="w-8 h-8 rounded bg-muted flex items-center justify-center flex-shrink-0">
                       {client.logo_url ? (
                         <img 
                           src={client.logo_url} 
                           alt={`${client.name} logo`}
                           className="w-full h-full object-cover rounded"
                         />
                       ) : (
                         <Building2 className="h-4 w-4 text-muted-foreground" />
                       )}
                     </div>
                     
                     <div className="flex-1 min-w-0">
                       <h3 className="font-medium text-foreground truncate">{client.name}</h3>
                       <p className="text-sm text-muted-foreground truncate">
                         {client.industry || '—'}
                       </p>
                     </div>
                   </div>
                   <span className="text-base" title={client.country || ''} aria-label={client.country || undefined}>
                     {getCountryFlag(client.country) || ''}
                   </span>
                 </div>
                
                {/* KPI Badges */}
                <ClientKPIBadges 
                  kpiData={kpiData}
                  isLoading={kpisLoading}
                />
                
              </div>
            );
          })}
        </div>
      </div>

      {/* Right Content - Client Details */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border bg-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-3 mb-2">
                <h1 className="text-2xl font-bold text-foreground truncate">{selectedClient?.name}</h1>
              </div>
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <span className={`inline-block w-2 h-2 rounded-full ${
                  selectedClient?.status === 'active' ? 'bg-green-500' :
                  selectedClient?.status === 'prospect' ? 'bg-blue-500' : 'bg-gray-500'
                }`}></span>
                <span title={selectedClient?.country || ''} aria-label={selectedClient?.country || undefined}>{getCountryFlag(selectedClient?.country) || selectedClient?.country}</span>
                <span>•</span>
                <span>{selectedClient?.industry}</span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={handleEditButtonClick}>
                <Edit className="h-4 w-4" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Client</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete "{selectedClient?.name}"? This action cannot be undone and will also remove all associated projects, proposals, and activities.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handleDeleteClient}
                      className="bg-red-600 hover:bg-red-700"
                      disabled={deleteClientMutation.isPending}
                    >
                      {deleteClientMutation.isPending ? "Deleting..." : "Delete Client"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>

        </div>

        {/* Tabs */}
        <div className="flex-1 p-6 overflow-hidden">
          <Tabs defaultValue="overview" className="h-full flex flex-col">
            <TabsList className="mb-6">
              <TabsTrigger value="overview"><TrendingUp className="h-4 w-4 mr-2" />Overview</TabsTrigger>
              <TabsTrigger value="activities"><Activity className="h-4 w-4 mr-2" />Activities</TabsTrigger>
              <TabsTrigger value="proposals"><FileText className="h-4 w-4 mr-2" />Proposals</TabsTrigger>
              <TabsTrigger value="business"><TrendingUp className="h-4 w-4 mr-2" />Business</TabsTrigger>
              <TabsTrigger value="notes"><StickyNote className="h-4 w-4 mr-2" />Notes</TabsTrigger>
              <TabsTrigger value="people"><Users className="h-4 w-4 mr-2" />People</TabsTrigger>
              <TabsTrigger value="files"><Download className="h-4 w-4 mr-2" />Files</TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-y-auto">
              <TabsContent value="overview" className="space-y-6 mt-0">
                {/* Overview Metrics */}
                 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                   <div>
                     <p className="text-sm text-muted-foreground">Total Value</p>
                     <p className="text-2xl font-bold text-foreground">€{clientStatistics.totalValue.toLocaleString()}</p>
                   </div>
                   <div>
                     <p className="text-sm text-muted-foreground">Proposals</p>
                     <p className="text-2xl font-bold text-foreground">{clientStatistics.proposalCount}</p>
                   </div>
                   <div>
                     <p className="text-sm text-muted-foreground">Funnel Stage</p>
                     <Badge className={funnelStageColors[selectedClient?.funnel_stage as keyof typeof funnelStageColors]}>
                       {funnelStageDisplayMap[selectedClient?.funnel_stage as keyof typeof funnelStageDisplayMap]}
                     </Badge>
                   </div>
                   <div>
                     <p className="text-sm text-muted-foreground">Last Activity</p>
                     <p className="text-lg font-bold text-foreground">
                       {clientStatistics.lastActivity ? format(new Date(clientStatistics.lastActivity), "MMM dd") : "None"}
                     </p>
                   </div>
                 </div>

                 {/* Client Information Card */}
                 <Card>
                   <CardHeader>
                     <CardTitle className="flex items-center space-x-2">
                       <Building2 className="h-5 w-5" />
                       <span>Client Information</span>
                     </CardTitle>
                   </CardHeader>
                   <CardContent className="space-y-4">
                     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                       <div>
                         <p className="text-sm font-medium text-muted-foreground">Industry</p>
                         <p className="text-sm text-foreground">{selectedClient?.industry || '—'}</p>
                       </div>
                       <div>
                         <p className="text-sm font-medium text-muted-foreground">Country</p>
                         <div className="flex items-center space-x-2">
                           <span>{getCountryFlag(selectedClient?.country)}</span>
                           <p className="text-sm text-foreground">{selectedClient?.country || '—'}</p>
                         </div>
                       </div>
                       <div>
                         <p className="text-sm font-medium text-muted-foreground">Status</p>
                         <Badge className={statusColors[selectedClient?.status as keyof typeof statusColors]}>
                           {selectedClient?.status}
                         </Badge>
                       </div>
                       <div>
                         <p className="text-sm font-medium text-muted-foreground">Payment Terms</p>
                         <p className="text-sm text-foreground">{selectedClient?.payment_terms || '60 Days'}</p>
                       </div>
                       <div>
                         <p className="text-sm font-medium text-muted-foreground">Logo</p>
                         <div className="w-12 h-12 rounded bg-muted flex items-center justify-center">
                           {selectedClient?.logo_url ? (
                             <img 
                               src={selectedClient.logo_url} 
                               alt={`${selectedClient.name} logo`}
                               className="w-full h-full object-cover rounded"
                             />
                           ) : (
                             <Building2 className="h-6 w-6 text-muted-foreground" />
                           )}
                         </div>
                       </div>
                     </div>
                   </CardContent>
                 </Card>

                {selectedClient?.id ? (
                  <HighlightedClientNotes clientId={selectedClient.id} />
                ) : null}

                {selectedClient?.id ? (
                  <ClientExploitation clientId={selectedClient.id} />
                ) : null}
              </TabsContent>

              <TabsContent value="activities" className="mt-0">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center space-x-2">
                      <Activity className="h-5 w-5" />
                      <span>{selectedClient?.name}</span>
                    </CardTitle>
                    <Button 
                      className="bg-primary hover:bg-primary/90"
                      onClick={() => navigate(`/activities?client_id=${selectedClient.id}`)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      New Activity
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {selectedClientDetails?.activities?.length > 0 ? (
                      <div className="space-y-4">
                        {selectedClientDetails.activities.map((activity: any) => {
                          const activityType = activityTypes.find(type => type.value === activity.type);
                          const StatusIcon = activityType?.icon || FileText;

                          return (
                             <div key={activity.id} className="p-4 border rounded-lg hover:bg-muted/30 transition-colors">
                               <div className="flex items-start justify-between">
                                 <div className="flex items-start space-x-4 flex-1">
                                   <div className={cn("p-2 rounded-full", activityType?.bgColor || "bg-gray-50")}>
                                     <StatusIcon className={cn("h-5 w-5", activityType?.iconColor || "text-gray-600")} />
                                   </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-2">
                                        <h3 className="font-semibold text-foreground">
                                          {activity.initiatives?.name || activity.subject || "Activity"}
                                        </h3>
                                      </div>
                                      
                                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                        {activity.clients && (
                                          <div className="flex items-center gap-1">
                                            <Building className="h-3 w-3" />
                                            <span>{activity.clients.name}</span>
                                          </div>
                                        )}
                                        
                                        {activity.people && activity.people.length > 0 && (
                                          <div className="flex items-center gap-1">
                                            <User className="h-3 w-3" />
                                            <span>{activity.people.map((p: any) => `${p.first_name} ${p.last_name}`).join(', ')}</span>
                                          </div>
                                        )}
                                      </div>
                                      
                                      {activity.description && (
                                        <p className="text-sm text-muted-foreground mt-2">{activity.description}</p>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <div className="flex flex-col items-end gap-2">
                                    <div className="text-xs text-muted-foreground">
                                      {format(new Date(activity.activity_date), "MMM dd, yyyy")}
                                    </div>
                                    {/* User info with avatar */}
                                    {(activity.assignee || activity.creator) && (
                                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                        {(activity.assignee?.profile_image_url || activity.creator?.profile_image_url) && (
                                          <img 
                                            src={activity.assignee?.profile_image_url || activity.creator?.profile_image_url} 
                                            alt="User avatar"
                                            className="w-6 h-6 rounded-full object-cover"
                                          />
                                        )}
                                        <div className="flex flex-col">
                                          <div className="font-medium">
                                            {activity.assignee ? 
                                              (activity.assignee.full_name || `${activity.assignee.first_name || ''} ${activity.assignee.last_name || ''}`.trim() || 'Unknown User') :
                                              (activity.creator?.full_name || `${activity.creator?.first_name || ''} ${activity.creator?.last_name || ''}`.trim() || 'Unknown User')
                                            }
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                    {activity.duration_minutes && (
                                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                        <Clock className="h-3 w-3" />
                                        <span>{activity.duration_minutes} min</span>
                                      </div>
                                    )}
                                  </div>
                               </div>
                               
                               {/* Control row with separator - same as Activities page */}
                               <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                                 {/* Status controls row */}
                                 <div className="flex items-center gap-4">
                                   <div className="flex items-center gap-2">
                                     <Label htmlFor={`done-switch-${activity.id}`} className="text-sm text-muted-foreground">
                                       Done
                                     </Label>
                                     <Switch
                                       id={`done-switch-${activity.id}`}
                                       checked={activity.status === 'Done'}
                                       onCheckedChange={async (checked) => {
                                         try {
                                           const { error } = await supabase
                                             .from("activities")
                                             .update({ status: checked ? 'Done' : 'Pending' })
                                             .eq('id', activity.id);

                                           if (error) throw error;
                                           
                                           // Refresh client details to update the activity list
                                           queryClient.invalidateQueries({ queryKey: ["client-details", selectedClient?.id] });
                                           toast({
                                             title: "Success",
                                             description: "Activity status updated",
                                           });
                                         } catch (error: any) {
                                           toast({
                                             title: "Error",
                                             description: error.message || "Failed to update status",
                                             variant: "destructive",
                                           });
                                         }
                                       }}
                                     />
                                   </div>

                                   {/* Show response status if available */}
                                   {activity.response_status && (
                                     <div className="flex items-center gap-2">
                                       <Popover>
                                         <PopoverTrigger asChild>
                                           <button
                                             className={cn(
                                               "px-3 py-1 text-xs rounded-full border cursor-pointer hover:opacity-80 transition-opacity min-h-[32px] touch-manipulation",
                                               responseStatusOptions.find(s => s.value === activity.response_status)?.color || "bg-gray-100 text-gray-700"
                                             )}
                                           >
                                             {responseStatusOptions.find(s => s.value === activity.response_status)?.label || activity.response_status}
                                           </button>
                                         </PopoverTrigger>
                                         <PopoverContent className="w-auto p-1 bg-background z-50 border shadow-lg" align="start">
                                           <div className="flex flex-col gap-1">
                                             {responseStatusOptions.map((status) => (
                                               <button
                                                 key={status.value}
                                                 className={cn(
                                                   "px-3 py-2 text-xs rounded-md text-left hover:bg-accent transition-colors min-h-[36px] touch-manipulation",
                                                   activity.response_status === status.value && "bg-accent"
                                                 )}
                                                 onClick={async () => {
                                                   try {
                                                     const { error } = await supabase
                                                       .from("activities")
                                                       .update({ response_status: status.value })
                                                       .eq('id', activity.id);

                                                     if (error) throw error;
                                                     
                                                     queryClient.invalidateQueries({ queryKey: ["client-details", selectedClient?.id] });
                                                     toast({
                                                       title: "Success",
                                                       description: "Response status updated",
                                                     });
                                                   } catch (error: any) {
                                                     toast({
                                                       title: "Error",
                                                       description: error.message || "Failed to update response status",
                                                       variant: "destructive",
                                                     });
                                                   }
                                                 }}
                                               >
                                                 <span className={cn("inline-block px-2 py-1 rounded text-xs border", status.color)}>
                                                   {status.label}
                                                 </span>
                                               </button>
                                             ))}
                                           </div>
                                         </PopoverContent>
                                       </Popover>
                                     </div>
                                   )}

                                   {/* Add response status if not set */}
                                   {!activity.response_status && (
                                     <div className="flex items-center gap-2">
                                       <Popover>
                                         <PopoverTrigger asChild>
                                           <button className="h-8 px-3 text-xs border border-dashed border-muted-foreground text-muted-foreground rounded-md hover:bg-accent transition-colors min-h-[32px] touch-manipulation">
                                             + Status
                                           </button>
                                         </PopoverTrigger>
                                         <PopoverContent className="w-auto p-1 bg-background z-50 border shadow-lg" align="start">
                                           <div className="flex flex-col gap-1">
                                             {responseStatusOptions.map((status) => (
                                               <button
                                                 key={status.value}
                                                 className="px-3 py-2 text-xs rounded-md text-left hover:bg-accent transition-colors min-h-[36px] touch-manipulation"
                                                 onClick={async () => {
                                                   try {
                                                     const { error } = await supabase
                                                       .from("activities")
                                                       .update({ response_status: status.value })
                                                       .eq('id', activity.id);

                                                     if (error) throw error;
                                                     
                                                     queryClient.invalidateQueries({ queryKey: ["client-details", selectedClient?.id] });
                                                     toast({
                                                       title: "Success",
                                                       description: "Response status updated",
                                                     });
                                                   } catch (error: any) {
                                                     toast({
                                                       title: "Error",
                                                       description: error.message || "Failed to update response status",
                                                       variant: "destructive",
                                                     });
                                                   }
                                                 }}
                                               >
                                                 <span className={cn("inline-block px-2 py-1 rounded text-xs border", status.color)}>
                                                   {status.label}
                                                 </span>
                                               </button>
                                             ))}
                                           </div>
                                         </PopoverContent>
                                       </Popover>
                                     </div>
                                   )}
                                 </div>

                                 {/* Action buttons row */}
                                 <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                   {/* Edit button */}
                                   <Button
                                     variant="ghost"
                                     size="sm"
                                     className="h-6 w-6 p-0 hover:bg-accent"
                                     onClick={() => navigate(`/activities?edit=${activity.id}`)}
                                   >
                                     <Edit className="h-3 w-3" />
                                   </Button>
                                   
                                   {/* Delete button */}
                                   <AlertDialog>
                                     <AlertDialogTrigger asChild>
                                       <Button
                                         variant="ghost"
                                         size="sm"
                                         className="h-6 w-6 p-0 hover:bg-destructive hover:text-destructive-foreground"
                                       >
                                         <Trash2 className="h-3 w-3" />
                                       </Button>
                                     </AlertDialogTrigger>
                                     <AlertDialogContent>
                                       <AlertDialogHeader>
                                         <AlertDialogTitle>Delete Activity</AlertDialogTitle>
                                         <AlertDialogDescription>
                                           Are you sure you want to delete this activity? This action cannot be undone.
                                         </AlertDialogDescription>
                                       </AlertDialogHeader>
                                       <AlertDialogFooter>
                                         <AlertDialogCancel>Cancel</AlertDialogCancel>
                                         <AlertDialogAction
                                           className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                           onClick={async () => {
                                             try {
                                               const { error } = await supabase
                                                 .from("activities")
                                                 .delete()
                                                 .eq('id', activity.id);

                                               if (error) throw error;
                                               
                                               queryClient.invalidateQueries({ queryKey: ["client-details", selectedClient?.id] });
                                               toast({
                                                 title: "Success",
                                                 description: "Activity deleted successfully",
                                               });
                                             } catch (error: any) {
                                               toast({
                                                 title: "Error",
                                                 description: error.message || "Failed to delete activity",
                                                 variant: "destructive",
                                               });
                                             }
                                           }}
                                         >
                                           Delete
                                         </AlertDialogAction>
                                       </AlertDialogFooter>
                                     </AlertDialogContent>
                                   </AlertDialog>
                                 </div>
                               </div>
                             </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-lg font-medium mb-2">No activities found</h3>
                        <p className="text-muted-foreground mb-4">
                          No activities have been recorded for this client yet.
                        </p>
                        <Button 
                          variant="outline"
                          onClick={() => navigate(`/activities?client_id=${selectedClient.id}`)}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add First Activity
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>


              <TabsContent value="proposals" className="mt-0">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center space-x-2">
                      <FileText className="h-5 w-5" />
                      <span>Proposals for {selectedClient?.name}</span>
                    </CardTitle>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2 text-sm">
                        <Label>Filter:</Label>
                        <Select value={proposalFilter} onValueChange={(value) => setProposalFilter(value as any)}>
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="accepted">Accepted</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="sent">Sent</SelectItem>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Label>Period:</Label>
                        <Select value={proposalTimeRange} onValueChange={(value) => setProposalTimeRange(value as any)}>
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ytd">This Year</SelectItem>
                            <SelectItem value="last_year">Last Year</SelectItem>
                            <SelectItem value="ever">All Time</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate(`/proposals?client_id=${selectedClient.id}`)}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        New Proposal
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="text-center p-4 bg-muted rounded-lg">
                        <p className="text-2xl font-bold text-foreground">{proposalsCount}</p>
                        <p className="text-sm text-muted-foreground">Proposals</p>
                      </div>
                      <div className="text-center p-4 bg-muted rounded-lg">
                        <p className="text-2xl font-bold text-foreground">€{proposalsTotal.toLocaleString()}</p>
                        <p className="text-sm text-muted-foreground">Total Value</p>
                      </div>
                    </div>
                    
                    {filteredProposals.length > 0 ? (
                      <div className="space-y-4">
                        {filteredProposals.map((proposal: any) => (
                          <div key={proposal.id} className="p-4 border rounded-lg hover:bg-muted/30 transition-colors">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <h4 className="font-medium text-foreground">{proposal.name}</h4>
                                  <Badge className={proposalStatusColors[proposal.status as keyof typeof proposalStatusColors]}>
                                    {proposal.status}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                  <div className="flex items-center gap-1">
                                    <DollarSign className="h-3 w-3" />
                                    <span>€{(proposal.final_value || proposal.value || 0).toLocaleString()}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <CalendarIcon className="h-3 w-3" />
                                    <span>{format(new Date(proposal.created_at), "MMM dd, yyyy")}</span>
                                  </div>
                                </div>
                                {proposal.description && (
                                  <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{proposal.description}</p>
                                )}
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/proposals/${proposal.id}`)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-lg font-medium mb-2">No proposals found</h3>
                        <p className="text-muted-foreground mb-4">
                          {proposalFilter === 'all' 
                            ? "No proposals have been created for this client yet."
                            : `No ${proposalFilter} proposals found for the selected period.`
                          }
                        </p>
                        <Button 
                          variant="outline"
                          onClick={() => navigate(`/proposals?client_id=${selectedClient.id}`)}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Create First Proposal
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="business" className="mt-0">
                {selectedClient?.id ? (
                  <ClientBusinessTab clientId={selectedClient.id} clientName={selectedClient.name} />
                ) : null}
              </TabsContent>

              <TabsContent value="people" className="mt-0">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center space-x-2">
                      <Users className="h-5 w-5" />
                      <span>People at {selectedClient?.name}</span>
                    </CardTitle>
                    <div className="flex items-center space-x-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => queryClient.invalidateQueries({ queryKey: ["client-details", selectedClient?.id] })}
                        title="Refresh people list"
                      >
                        <Clock className="h-4 w-4" />
                      </Button>
                       <Button 
                         variant="outline" 
                         size="sm"
                         onClick={handleAddPerson}
                       >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Person
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {selectedClientDetails?.people?.length > 0 ? (
                      selectedClientDetails.people.map((person: any) => (
                         <div key={person.id} className="flex items-start justify-between p-4 border rounded-lg hover:bg-muted/30 transition-colors">
                           <div className="flex-1">
                             <div className="flex items-center space-x-3 mb-2">
                               <div className="flex-1">
                                 {editingPersons[person.id] ? (
                                   <div className="space-y-2">
                                     <div className="flex gap-2">
                                       <Input
                                         placeholder="First Name"
                                         value={personEditData[person.id]?.first_name || ''}
                                         onChange={(e) => updatePersonEditData(person.id, 'first_name', e.target.value)}
                                         className="flex-1"
                                       />
                                       <Input
                                         placeholder="Last Name"
                                         value={personEditData[person.id]?.last_name || ''}
                                         onChange={(e) => updatePersonEditData(person.id, 'last_name', e.target.value)}
                                         className="flex-1"
                                       />
                                     </div>
                                     <div className="flex gap-2">
                                       <Button 
                                         size="sm"
                                         onClick={() => savePersonChanges(person.id)}
                                       >
                                         Save
                                       </Button>
                                       <Button 
                                         size="sm"
                                         variant="outline"
                                         onClick={() => cancelEditingPerson(person.id)}
                                       >
                                         Cancel
                                       </Button>
                                     </div>
                                   </div>
                                 ) : (
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <h4 className="font-medium text-foreground">
                                          {person.first_name || person.last_name 
                                            ? `${person.first_name} ${person.last_name}`.trim()
                                            : person.email || 'Unnamed Person'}
                                        </h4>
                                        {person.position && (
                                          <p className="text-sm text-muted-foreground">{person.position}</p>
                                        )}
                                      </div>
                                      <div className="flex items-center space-x-1">
                                        <Button 
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => startEditingPerson(person)}
                                          className="h-8 w-8 p-0"
                                        >
                                          <Edit className="h-4 w-4" />
                                        </Button>
                                        <AlertDialog>
                                          <AlertDialogTrigger asChild>
                                            <Button 
                                              size="sm"
                                              variant="ghost"
                                              className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
                                            >
                                              <Trash2 className="h-4 w-4" />
                                            </Button>
                                          </AlertDialogTrigger>
                                          <AlertDialogContent>
                                            <AlertDialogHeader>
                                              <AlertDialogTitle>Delete Person</AlertDialogTitle>
                                              <AlertDialogDescription>
                                                Are you sure you want to delete {person.first_name || person.last_name 
                                                  ? `${person.first_name} ${person.last_name}`.trim()
                                                  : person.email || 'this person'}? 
                                                This action cannot be undone and will also delete all related employment history, activities, and proposal recipients.
                                              </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                                              <AlertDialogAction 
                                                onClick={() => deletePersonMutation.mutate(person.id)}
                                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                                disabled={deletePersonMutation.isPending}
                                              >
                                                {deletePersonMutation.isPending ? "Deleting..." : "Delete"}
                                              </AlertDialogAction>
                                            </AlertDialogFooter>
                                          </AlertDialogContent>
                                        </AlertDialog>
                                      </div>
                                    </div>
                                 )}
                               </div>
                               {person.is_decision_maker && (
                                 <Badge variant="secondary" className="text-xs">
                                   <Crown className="h-3 w-3 mr-1" />
                                   Decision Maker
                                 </Badge>
                               )}
                             </div>
                            
                            <div className="flex items-center space-x-4 text-sm text-muted-foreground mb-2">
                              {person.email && (
                                <div className="flex items-center space-x-1">
                                  <Mail className="h-3 w-3" />
                                  <span>{person.email}</span>
                                </div>
                              )}
                              {person.phone && (
                                <div className="flex items-center space-x-1">
                                  <Phone className="h-3 w-3" />
                                  <span>{person.phone}</span>
                                </div>
                              )}
                              {person.linkedin_url && (
                                <div className="flex items-center space-x-1">
                                  <LinkedinIcon className="h-3 w-3" />
                                  <a 
                                    href={person.linkedin_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-primary hover:underline"
                                  >
                                    LinkedIn
                                  </a>
                                </div>
                              )}
                            </div>

                            {person.tags && person.tags.length > 0 && (
                              <div className="flex items-center space-x-2 mb-2">
                                <Tag className="h-3 w-3 text-muted-foreground" />
                                <div className="flex flex-wrap gap-1">
                                  {person.tags.map((tag: string, index: number) => (
                                    <Badge key={index} variant="outline" className="text-xs">
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {person.notes && (
                              <p className="text-sm text-muted-foreground mt-2">{person.notes}</p>
                            )}
                          </div>
                          
                          <Badge 
                            variant="outline" 
                            className={`ml-4 ${
                              person.status === 'active' ? 'text-green-600 border-green-200' : 'text-gray-600 border-gray-200'
                            }`}
                          >
                            {person.status}
                          </Badge>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-lg font-medium mb-2">No people found</h3>
                        <p className="text-muted-foreground mb-4">
                          No contacts have been added for this client yet.
                        </p>
                        <Button 
                          variant="outline"
                          onClick={() => navigate(`/people?client_id=${selectedClient.id}`)}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add First Contact
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="projects" className="mt-0">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center space-x-2">
                      <Target className="h-5 w-5" />
                      <span>Projects for {selectedClient?.name}</span>
                    </CardTitle>
                     <Button 
                       variant="outline" 
                       size="sm"
                        onClick={() => navigate(`/projects?client_id=${selectedClient.id}`)}
                     >
                       <Plus className="h-4 w-4 mr-2" />
                       New Project
                     </Button>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {selectedClientDetails?.projects?.length > 0 ? (
                      selectedClientDetails.projects.map((project: any) => (
                        <div key={project.id} className="p-4 border rounded-lg hover:bg-muted/30 transition-colors">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h4 className="font-medium text-foreground mb-1">{project.name}</h4>
                              {project.description && (
                                <p className="text-sm text-muted-foreground mb-2">{project.description}</p>
                              )}
                            </div>
                            <Badge className={`ml-4 ${projectStatusColors[project.status as keyof typeof projectStatusColors]}`}>
                              {project.status?.replace('_', ' ')}
                            </Badge>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            {project.value && (
                              <div className="flex items-center space-x-2">
                                <DollarSign className="h-4 w-4 text-muted-foreground" />
                                <span>€{Number(project.value).toLocaleString()}</span>
                              </div>
                            )}
                            
                             {project.start_date && (
                               <div className="flex items-center space-x-2">
                                 <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                                 <span>Start: {format(new Date(project.start_date), "MMM dd, yyyy")}</span>
                               </div>
                             )}
                            
                            {project.delivery_date && (
                              <div className="flex items-center space-x-2">
                                <Target className="h-4 w-4 text-muted-foreground" />
                                <span>Due: {format(new Date(project.delivery_date), "MMM dd, yyyy")}</span>
                              </div>
                            )}
                            
                            <div className="flex items-center space-x-2">
                              <Badge variant="outline" className="text-xs">
                                {project.type || 'adhoc'}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-lg font-medium mb-2">No projects found</h3>
                        <p className="text-muted-foreground mb-4">
                          No projects have been created for this client yet.
                        </p>
                        <Button 
                          variant="outline"
                          onClick={() => navigate(`/projects?client_id=${selectedClient.id}`)}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Create First Project
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="notes" className="mt-0">
                {selectedClient ? (
                  <ClientNotes clientId={selectedClient.id} />
                ) : (
                  <Card>
                    <CardContent className="py-8 text-center text-muted-foreground">
                      Select a client to view notes.
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="files" className="mt-0">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center space-x-2">
                      <FileText className="h-5 w-5" />
                      <span>Files for {selectedClient?.name}</span>
                    </CardTitle>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleFileUpload}
                      disabled={uploadFileMutation.isPending}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {uploadFileMutation.isPending ? 'Uploading...' : 'Upload File'}
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {selectedClientDetails?.documents?.length > 0 ? (
                      selectedClientDetails.documents.map((document: any) => (
                        <div key={document.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/30 transition-colors">
                          <div className="flex items-center space-x-3 flex-1">
                            <FileText className="h-8 w-8 text-muted-foreground flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-foreground truncate">{document.name}</h4>
                              <div className="flex items-center space-x-2 text-sm text-muted-foreground mt-1">
                                <span>{document.mime_type || 'Unknown type'}</span>
                                {document.size_bytes && (
                                  <>
                                    <span>•</span>
                                    <span>{(document.size_bytes / 1024 / 1024).toFixed(1)} MB</span>
                                  </>
                                )}
                                <span>•</span>
                                <span>Uploaded {format(new Date(document.created_at), "MMM dd, yyyy")}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={async () => {
                                if (document.file_path && document.drive_url) {
                                  try {
                                    // Extract bucket name from drive_url
                                    const bucketMatch = document.drive_url.match(/\/storage\/v1\/object\/public\/([^\/]+)\//);
                                    const bucketName = bucketMatch ? bucketMatch[1] : 'public-files';
                                    
                                    const { data, error } = await supabase.storage
                                      .from(bucketName)
                                      .download(document.file_path);
                                    
                                    if (error) throw error;
                                    
                                    const url = window.URL.createObjectURL(data);
                                    const a = window.document.createElement('a');
                                    a.href = url;
                                    a.download = document.name;
                                    window.document.body.appendChild(a);
                                    a.click();
                                    window.URL.revokeObjectURL(url);
                                    window.document.body.removeChild(a);
                                  } catch (error) {
                                    toast({
                                      title: "Error",
                                      description: "Failed to download file",
                                      variant: "destructive",
                                    });
                                  }
                                }
                              }}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-lg font-medium mb-2">No files found</h3>
                        <p className="text-muted-foreground mb-4">
                          No documents have been uploaded for this client yet.
                        </p>
                        <Button 
                          variant="outline"
                          onClick={handleFileUpload}
                          disabled={uploadFileMutation.isPending}
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {uploadFileMutation.isPending ? 'Uploading...' : 'Upload First File'}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>
    </div>
  );
}